import React from "react"
import CModalBase from "./c_modal_base"
import { createPortal } from "react-dom"

/**
 * The modal uses javascript logic outside react.
 * It has to be opened using a function of the modal element:
 * <code>modalRef.current.dfbModal.open()</code>
 * Where "modalRef" is the reference that is passed to the modal.
 *
 * @param ref The reference that can be used to access the modal element (see above)
 * @param {string} props.id The id of the modal (required)
 * @param {string} props.headline The headline (required)
 * @param {string} props.cancelButton The label for the cancel button
 * @param {string} props.actionButton The label for the action button
 * @param {string} props.onAction The function that is triggered on click of the action button
 * @param {string} props.children The additional content that is shown between headline and buttons
 * @param {boolean} props.hideCloseIcon Set to true to hide the close icon (default false)
 * @param {boolean} props.autoOpen When set to true the modal is opened directly
 */
const CModal = React.forwardRef(
  (
    {
      id,
      autoOpen,
      headline,
      cancelButton,
      actionButton,
      hideCloseIcon,
      onAction,
      onCancel,
      children,
      add_class,
    },
    ref
  ) => {
    return (
      <div>
        {/* Using a portal we ensure the dfb-ui logic does not need to move the
        portal directly below the body tag, which would prevent the React 18
        event handler from working */}
        {createPortal(
          <CModalBase
            ref={ref}
            id={id}
            autoOpen={autoOpen}
            hideCloseIcon={hideCloseIcon}
            add_class={add_class}
          >
            {headline && (
              <div className="dfb-Modal-modalHeader" data-testid="modal-headline">
                <h1 className="dfb-Headline dfb-Headline--h4">{headline}</h1>
              </div>
            )}
            {children && (
              <div className="dfb-Modal-modalContent">{children}</div>
            )}
            {(cancelButton || actionButton) && (
              <div className="dfb-Modal-modalFooter">
                <div className="dfb-ButtonGroup dfb-ButtonGroup--stacked dfb-Modal-buttonGroup">
                  {cancelButton && (
                    <button
                      className="dfb-Button dfb-Button--secondary dfb-ButtonGroup-button"
                      id="dfb-secondaryButton"
                      type="button"
                      data-dismiss={!onCancel ? "modal" : undefined}
                      onClick={onCancel}
                    >
                      <span className="dfb-Button-label">{cancelButton}</span>
                    </button>
                  )}
                  {actionButton && onAction && (
                    <button
                      className="dfb-Button dfb-ButtonGroup-button"
                      onClick={onAction}
                      id="dfb-primaryButton"
                      type="button"
                    >
                      <span className="dfb-Button-label">{actionButton}</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </CModalBase>,
          document.body
        )}
      </div>
    )
  }
)

export default CModal
